import React, { useState } from 'react';
import emailjs from '@emailjs/browser';
import { 
  Mail, 
  MapPin, 
  Clock,
  Send,
  User,
  Phone,
  MessageSquare,
  Loader2
} from 'lucide-react';
import { 
  trackFormSubmitAttempt, 
  trackFormSubmitSuccess, 
  trackFormSubmitError 
} from './GoogleAnalytics';

const Contact: React.FC = () => {
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error'>('success');
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    message: ''
  });
  const [errors, setErrors] = useState({
    name: '',
    phone: ''
  });

  // Configuração do EmailJS
  const EMAILJS_SERVICE_ID = 'service_yv8ipdm';
  const EMAILJS_TEMPLATE_ID = 'template_40p2t39';
  const EMAILJS_PUBLIC_KEY = '69H5daZOSfRe-aPNF';



  const showToastMessage = (message: string, type: 'success' | 'error') => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 4000);
  };

  // Função para formatar telefone
  const formatPhone = (value: string) => {
    // Remove todos os caracteres não numéricos
    const numbers = value.replace(/\D/g, '');
    
    // Limita a 11 dígitos
    const limitedNumbers = numbers.slice(0, 11);
    
    // Aplica a formatação
    if (limitedNumbers.length <= 2) {
      return limitedNumbers;
    } else if (limitedNumbers.length <= 7) {
      return `(${limitedNumbers.slice(0, 2)}) ${limitedNumbers.slice(2)}`;
    } else {
      return `(${limitedNumbers.slice(0, 2)}) ${limitedNumbers.slice(2, 7)}-${limitedNumbers.slice(7)}`;
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear errors when user starts typing
    if (errors[name as keyof typeof errors]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formattedValue = formatPhone(e.target.value);
    setFormData(prev => ({ ...prev, phone: formattedValue }));
    
    // Clear errors when user starts typing
    if (errors.phone) {
      setErrors(prev => ({ ...prev, phone: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = { name: '', phone: '' };
    let isValid = true;

    if (!formData.name.trim()) {
      newErrors.name = 'Nome é obrigatório';
      isValid = false;
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Telefone é obrigatório';
      isValid = false;
    } else {
      // Remove formatação para validar apenas os números
      const phoneNumbers = formData.phone.replace(/\D/g, '');
      if (phoneNumbers.length < 10 || phoneNumbers.length > 11) {
        newErrors.phone = 'Telefone deve ter 10 ou 11 dígitos';
        isValid = false;
      }
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      showToastMessage('Por favor, corrija os erros no formulário', 'error');
      return;
    }

    // Track form submission attempt
    trackFormSubmitAttempt(formData);

    setIsLoading(true);

    try {
      // EmailJS send email
      const templateParams = {
        name: formData.name,
        phone: formData.phone,
        message: formData.message || 'Sem mensagem adicional',
        time: new Date().toLocaleString('pt-BR')
      };

      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        templateParams,
        EMAILJS_PUBLIC_KEY
      );

      // Track successful form submission
      trackFormSubmitSuccess(formData);

      showToastMessage('Mensagem enviada com sucesso! Entraremos em contato em breve.', 'success');
      
      // Reset form
      setFormData({ name: '', phone: '', message: '' });
      setErrors({ name: '', phone: '' });
      
    } catch (error) {
      console.error('Erro ao enviar email:', error);
      
      // Track form submission error
      trackFormSubmitError(error, formData);
      
      showToastMessage('Erro ao enviar mensagem. Tente novamente ou use o email direto.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {/* Toast */}
      {showToast && (
        <div className={`fixed top-4 right-4 px-4 py-2 rounded-lg shadow-lg z-[60] animate-fade-in text-white ${
          toastType === 'success' ? 'bg-green-500' : 'bg-red-500'
        }`}>
          {toastMessage}
        </div>
      )}
      
      <section id="contato" className="py-16 px-6 sm:px-8 sm:py-20 lg:py-24 bg-purple-50">
      <div className="container-custom">
        {/* Header da seção */}
        <div className="text-center mb-12 sm:mb-16 lg:mb-20">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6 sm:mb-8">
            Entre em Contato
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Pronto para iniciar seu tratamento de fisioterapia ou Pilates? Entre em contato para agendar sua avaliação.
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          {/* Informações de contato */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 sm:gap-10 mb-12 sm:mb-16">
            <div className="flex items-start space-x-4 sm:space-x-5">
              <div className="w-14 h-14 sm:w-16 sm:h-16 bg-accent-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Mail className="w-6 h-6 sm:w-7 sm:h-7 text-accent-500" />
              </div>
              <div>
                <h4 className="text-sm sm:text-base font-semibold text-gray-900 mb-2">E-mail</h4>
                <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">contato@katiahondafisio.com.br</p>
              </div>
            </div>

            <div className="flex items-start space-x-4 sm:space-x-5">
              <div className="w-14 h-14 sm:w-16 sm:h-16 bg-secondary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <MapPin className="w-6 h-6 sm:w-7 sm:h-7 text-secondary-500" />
              </div>
              <div>
                <h4 className="text-sm sm:text-base font-semibold text-gray-900 mb-2">Área de Atendimento</h4>
                <p className="text-xs sm:text-sm text-gray-600 leading-relaxed"><strong>Zona Sul de São Paulo:</strong> Jabaquara, São Judas, Conceição, Saúde, Vila Mariana e regiões próximas</p>
              </div>
            </div>

            <div className="flex items-start space-x-4 sm:space-x-5">
              <div className="w-14 h-14 sm:w-16 sm:h-16 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Clock className="w-6 h-6 sm:w-7 sm:h-7 text-primary-500" />
              </div>
              <div>
                <h4 className="text-sm sm:text-base font-semibold text-gray-900 mb-2">Horários de Atendimento</h4>
                <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">Segunda à sexta: 15h às 19h</p>
              </div>
            </div>
          </div>
         

          {/* Formulário de Contato */}
          <div id="formulario-contato" className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-xl p-6 sm:p-8 lg:p-10 border border-primary-200">
            <h4 className="text-lg sm:text-xl md:text-2xl font-semibold text-primary-900 mb-8 sm:mb-10 text-center">
              Formulário de Contato
            </h4>
            <form onSubmit={handleSubmit} className="space-y-8">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8">
                {/* Nome */}
                <div>
                  <label htmlFor="name" className="block text-sm sm:text-base font-medium text-primary-800 mb-3">
                    Nome *
                  </label>
                  <div className="relative">
                    <User className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-primary-400" />
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className={`w-full pl-12 pr-4 py-4 text-base border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors min-h-[48px] ${
                        errors.name ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Seu nome completo"
                    />
                  </div>
                  {errors.name && <p className="mt-2 text-sm text-red-600">{errors.name}</p>}
                </div>

                {/* Telefone */}
                <div>
                  <label htmlFor="phone" className="block text-sm sm:text-base font-medium text-primary-800 mb-3">
                    Telefone com DDD *
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-primary-400" />
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handlePhoneChange}
                      className={`w-full pl-12 pr-4 py-4 text-base border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors min-h-[48px] ${
                        errors.phone ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="(11) 99999-9999"
                      maxLength={15}
                    />
                  </div>
                  {errors.phone && <p className="mt-2 text-sm text-red-600">{errors.phone}</p>}
                </div>
              </div>

              {/* Mensagem */}
              <div>
                <label htmlFor="message" className="block text-sm sm:text-base font-medium text-primary-800 mb-3">
                  Mensagem (opcional)
                </label>
                <div className="relative">
                  <MessageSquare className="absolute left-4 top-4 w-5 h-5 text-primary-400" />
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={5}
                    className="w-full pl-12 pr-4 py-4 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors resize-none min-h-[120px]"
                    placeholder="Conte-nos sobre sua condição ou dúvidas..."
                  />
                </div>
              </div>

              {/* Botão */}
              <div className="flex justify-center pt-4">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="bg-primary-500 text-white font-semibold py-4 px-8 sm:px-10 text-base rounded-lg hover:bg-primary-600 transition-colors duration-300 inline-flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed min-h-[52px] w-full sm:w-auto"
                >
                  {isLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Send className="w-5 h-5" />
                  )}
                  <span>{isLoading ? 'Enviando...' : 'Enviar Contato'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      </section>
    </>
  );
};

export default Contact;